import { useCallback, useMemo } from "react";
import { proxy, useSnapshot } from "valtio";
import { IDashboardContext, useDashboardContext } from "../../../contexts/dashboard-context";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { transformPoint } from "../../../transform";
import { IDragImageRef } from "../../drag-image";

function canSwapPhoto(
	dashboard: Pick<IDashboardContext, "canSwapPhoto">,
	fromZone: eXpress.core.IZone | undefined,
	toZone: eXpress.core.IZone | undefined,
): fromZone is eXpress.core.PhotoZone {
	return (
		eXpress.core.isPhotoZone(toZone) && // Prevent dropping o a page background
		dashboard.canSwapPhoto(fromZone, toZone) &&
		fromZone.spread === toZone.spread
	);
}

function canEmptyZone(
	dashboard: Pick<IDashboardContext, "canRemovePhotoFromZones">,
	zone: eXpress.core.IZone | undefined,
): zone is eXpress.core.PhotoZone {
	return (
		eXpress.core.isPhotoZone(zone) && // Prevent dropping o a page background
		dashboard.canRemovePhotoFromZones([zone])
	);
}

/** Shared state */
const photoSwappingState = proxy({
	isPhotoSwapping: false,
	actions: {
		startPhotoSwapping() {
			photoSwappingState.isPhotoSwapping = true;
		},
		endPhotoSwapping() {
			photoSwappingState.isPhotoSwapping = false;
		},
	},
});

/**
 * Logic to swap a photo
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoSwapping(
	/** pass undefined if you only need the isPhotoSwapping state */
	dragImageRef: React.RefObject<IDragImageRef | null> | undefined,
) {
	const dashboard = useDashboardContext();
	const {
		client,
		editor: { logger },
	} = useAppContext();
	const { previewOverlayRef } = usePreviewContext();
	const googleAnalytics = useGoogleAnalytics();
	const { getSpreadToScreenTransform } = usePreviewContext();

	const dragImage = dragImageRef?.current;

	const mutable = useMemo<{
		startZone: eXpress.core.IZone | undefined;
		/** Used to know and remove the previous highlighted zone */
		lastHighlightedZone: eXpress.core.IZone | undefined;
		x: number;
		y: number;
	}>(
		() => ({
			startZone: undefined,
			lastHighlightedZone: undefined,
			x: 0,
			y: 0,
		}),
		[],
	);

	const { isPhotoSwapping } = useSnapshot(photoSwappingState);

	const getCenterOfZone = useCallback(
		function getCenterOfZone(startZone: eXpress.core.IZone | undefined | null) {
			if (!startZone) return undefined;
			const centerDip = startZone.spreadInnerBounds.center();
			const transform = getSpreadToScreenTransform(startZone.spread, logger);
			if (!transform) return undefined;
			return transformPoint(centerDip, transform);
		},
		[getSpreadToScreenTransform, logger],
	);

	/** Start dragging, animate from center of the zone to the passed x,y position */
	const start = useCallback(
		function photoSwappingStart(
			startZone: eXpress.core.IZone | undefined,
			x: number,
			y: number,
		) {
			window.addEventListener("touchend", () => dragImage?.end(), {
				once: true,
			});
			if (dashboard.canApplyPhotoToZone(startZone)) {
				mutable.startZone = startZone;
				photoSwappingState.actions.startPhotoSwapping();

				// Show Thumb of dragging zone
				const thumbUrl = eXpress.core.isPhotoZone(startZone)
					? startZone.getPhotoLook()?.photo.orientedThumbUrl?.getAbsoluteUrl(client)
					: undefined;
				if (thumbUrl) {
					const center = getCenterOfZone(startZone);
					dragImage?.start(
						thumbUrl,
						x,
						y,
						center ? center.left - x : undefined,
						center ? center.top - y : undefined,
					);
				}

				return true;
			}
			return false;
		},

		[client, dashboard, dragImage, getCenterOfZone, mutable],
	);

	const update = useCallback(
		function photoSwappingUpdate(
			dropZone: eXpress.core.IZone | undefined,
			x: number,
			y: number,
		) {
			const { startZone, lastHighlightedZone } = mutable;
			if (!startZone) return;

			// Update Drag Thumb
			dragImage?.moveTo(x, y);

			// Highlight Drop Target
			const isValidDropTarget =
				eXpress.core.isPhotoZone(dropZone) && canSwapPhoto(dashboard, startZone, dropZone);
			const validDropZone = isValidDropTarget ? dropZone : undefined;

			// Update UI
			if (validDropZone !== lastHighlightedZone) {
				previewOverlayRef.current?.updateZoneState(mutable.lastHighlightedZone, {
					highlightZone: false,
				});
				previewOverlayRef.current?.updateZoneState(validDropZone, {
					highlightZone: true,
				});
				mutable.lastHighlightedZone = validDropZone;
			}

			mutable.x = x;
			mutable.y = y;
		},
		[mutable, dragImage, dashboard, previewOverlayRef],
	);

	/**
	 * End dragging photo from zone
	 * - zone: swap with this zone, animate to center of target zone
	 * - null: remove photo from source zone, no animation
	 * - undefined: do nothing, animate back to start zone
	 */
	const end = useCallback(
		function photoSwappingEnd(endZone: eXpress.core.IZone | null | undefined) {
			const { startZone } = mutable;
			if (!startZone) return false;

			let isSwapped = false;
			let isRemoved = false;

			try {
				// Remove photo from Source?
				if (endZone === null) {
					if (canEmptyZone(dashboard, startZone)) {
						dashboard.removePhotoFromZones([startZone]);
						isRemoved = true;
					}
				} else if (
					// Swap photos in source and target
					canSwapPhoto(dashboard, startZone, endZone) &&
					eXpress.core.isPhotoZone(endZone)
				) {
					dashboard.swapPhoto(startZone, endZone);
					isSwapped = true;

					googleAnalytics.trackEventToGa({
						trackingAction: "PhotoSwap",
					});
				}
			} finally {
				// Remove Dragging Thumb
				let center: eXpress.math.IPoint | undefined;
				if (isSwapped) center = getCenterOfZone(endZone); // Fly to destination
				else if (!isRemoved) center = getCenterOfZone(startZone);
				// Fly back
				if (center) {
					dragImage?.end(center.left - mutable.x, center.top - mutable.y);
				} else {
					dragImage?.end();
				}
			}

			// Remove highlighted zone
			previewOverlayRef.current?.updateZoneState(mutable.lastHighlightedZone, {
				highlightZone: false,
			});

			mutable.startZone = undefined;
			photoSwappingState.actions.endPhotoSwapping();

			return isSwapped;
		},
		[mutable, previewOverlayRef, dashboard, googleAnalytics, getCenterOfZone, dragImage],
	);

	/** Immediately cancel dragging, no animation */
	const cancel = useCallback(
		function photoSwappingCancel() {
			const { lastHighlightedZone } = mutable;

			mutable.startZone = undefined;
			photoSwappingState.actions.endPhotoSwapping();

			// Remove dragging thumb
			dragImage?.end();

			// Remove highlighted zone
			previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
				highlightZone: false,
			});
		},
		[mutable, previewOverlayRef, dragImage],
	);

	return [
		isPhotoSwapping,
		useMemo(
			// Try to return a stable object so no need to destructure result
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
